/**
 * Teksten voor een nieuwe freelancer-mailaccount (Joris 28 sep): de welkomstmail (zonder wachtwoord)
 * en het losse app-bericht mét het tijdelijke wachtwoord — nooit samen in één kanaal.
 */
const voornaamVan = (naam) => String(naam || '').trim().split(/\s+/)[0] || '';

/** Personen.Functie is een multipleSelect: als lijst of als komma-string (fnStr-regel, CLAUDE.md). */
export const functieTekst = (functie) => (
  Array.isArray(functie) ? functie.filter(Boolean).join(', ') : String(functie ?? '')
);

export const VARIANT_PROJECTLEIDER = 'projectleider';
export const VARIANT_STANDAARD = 'standaard';

/** Projectleider-variant als de functie "Projectleider" bevat; anders (of zonder functie) de standaardtekst. */
export const welkomstVariant = (functie) => (
  /projectleider/i.test(functieTekst(functie)) ? VARIANT_PROJECTLEIDER : VARIANT_STANDAARD
);

export const VARIANT_LABEL = {
  [VARIANT_PROJECTLEIDER]: 'projectleider-variant',
  [VARIANT_STANDAARD]: 'standaardvariant',
};

/**
 * Welkomstmail. `variant` kiest expliciet (UI-keuze); zonder `variant` volgt hij uit `functie`.
 * De standaardtekst (crew/editors) is ongewijzigd; de projectleider-variant (Joris 27 okt) maakt de
 * PL zelf aanspreekpunt, laat prijs/offerte/meerwerk via accountmanager of MT lopen en voegt TOOLFRAME toe.
 */
export function welkomstmail({ naam, upn, functie, variant }) {
  const vn = voornaamVan(naam);
  const pl = (variant || welkomstVariant(functie)) === VARIANT_PROJECTLEIDER;
  const onderwerp = `Welkom: je Full Frame-mailadres ${upn}`;
  const waarom = pl
    ? `WAAROM EEN FULL FRAME-ADRES?
Als freelance projectleider werk je namens Full Frame met onze klanten. Met een Full Frame-adres:
- herkent de klant je meteen als onderdeel van het team en komt je mail niet in de spam terecht;
- blijft de correspondentie bij het project, zodat collega's kunnen meelezen of het overnemen als dat nodig is;
- houd je je privé-mail en je eigen klanten gescheiden van je werk voor Full Frame.`
    : `WAAROM EEN FULL FRAME-ADRES?
Als freelancer werk je namens Full Frame met onze klanten. Met een Full Frame-adres:
- herkent de klant je meteen als onderdeel van het team en komt je mail niet in de spam terecht;
- blijft de correspondentie bij het project, zodat de projectleider kan meelezen of het overnemen als dat nodig is;
- houd je je privé-mail en je eigen klanten gescheiden van je werk voor Full Frame.`;
  const wanneer = `WANNEER GEBRUIK JE HET?
Gebruik ${upn} voor alles wat je namens Full Frame doet:
- mail met klanten van Full Frame: afstemmen, planning, feedback, oplevering;
- afspraken met locaties, leveranciers of acteurs voor een Full Frame-productie;
${pl
    ? `- contact met collega's en freelancers die meewerken aan een Full Frame-project.
Gebruik het niet voor je eigen klanten, eigen offertes of privézaken; daarvoor blijf je je eigen adres gebruiken. Mailt een klant van Full Frame je toch op je privé-adres? Antwoord dan vanaf ${upn}.`
    : `- contact met collega's en andere freelancers over een Full Frame-project.
Gebruik het niet voor je eigen klanten, eigen offertes of privézaken; daarvoor blijf je je eigen adres gebruiken. Mailt een klant van Full Frame je toch op je privé-adres? Antwoord dan vanaf ${upn} en zet de projectleider in cc.`}`;
  const afspraken = pl
    ? `AFSPRAKEN
- Jij bent het aanspreekpunt voor de klant: planning, afstemming, feedback en oplevering.
- Prijzen, offertes en meerwerk stem je af met de accountmanager of het MT voordat je ze met de klant afspreekt.
- Grote bestanden deel je via Frame.io of de deelfunctie van Toolframe ("Bestanden delen"), niet als bijlage vanaf je privé-account.
- Houd collega's en freelancers die meewerken op de hoogte vanaf je Full Frame-adres.
- Niet doorsturen naar je privé-adres en geen klantmail opslaan buiten Full Frame. Deze mail is vertrouwelijk.`
    : `AFSPRAKEN
- Projectleider in cc bij belangrijke afspraken met de klant: planning, prijs, scope, oplevering. De projectleider houdt het overzicht en is het aanspreekpunt voor offerte en factuur.
- Geen prijzen of meerwerk afspreken zonder de projectleider.
- Grote bestanden deel je via Frame.io of de link die de projectleider je geeft, niet als bijlage vanaf je privé-account.
- Niet doorsturen naar je privé-adres en geen klantmail opslaan buiten Full Frame. Deze mail is vertrouwelijk.`;
  const inloggen = `EERSTE KEER INLOGGEN (±5 MINUTEN)
1. Installeer de app Microsoft Authenticator op je telefoon (App Store / Google Play).
2. Ga naar https://outlook.office.com
3. Log in met ${upn} en het tijdelijke wachtwoord dat je apart via WhatsApp krijgt.
4. Kies een nieuw wachtwoord (minimaal 8 tekens, niet een die je ergens anders gebruikt).
5. Beveilig je account: scan de QR-code met Microsoft Authenticator. Voortaan bevestig je het inloggen met je telefoon.`;
  const toolframe = pl
    ? `

TOOLFRAME
Met hetzelfde account log je in op https://intern.fullframe.nl voor projecten, taken en planning. Welke onderdelen je ziet, stel ik in. Lukt inloggen niet? Mail dan naar joris@fullframe.nl.`
    : '';
  const tekst = `Hoi ${vn},

Fijn dat je met ons aan de slag gaat. Voor je werk voor Full Frame heb je tijdelijk een eigen mailadres: ${upn}. In deze mail lees je waarom we dat doen, wanneer je het gebruikt en hoe je inlogt.

${waarom}

${wanneer}

${afspraken}

${inloggen}${toolframe}

WACHTWOORD VERGETEN OF NIEUWE TELEFOON?
Stuur een mail naar joris@fullframe.nl, dan stel ik een nieuw tijdelijk wachtwoord in of zet ik de beveiliging opnieuw klaar.

NA DE SAMENWERKING
Als de samenwerking stopt, sluiten we het account af. De mail blijft bij Full Frame, zodat lopende klantcontacten overgedragen kunnen worden.

Vragen? Bel of app me gerust.

Welkom en veel succes!

Groet,
Joris`;
  return { onderwerp, tekst };
}

/** Het losse bericht met het wachtwoord (WhatsApp/sms). `nieuw` = na een wachtwoord-reset. */
export function appBericht({ naam, upn, wachtwoord, nieuw = false }) {
  const vn = voornaamVan(naam);
  return nieuw
    ? `Hoi ${vn}, je nieuwe tijdelijke wachtwoord voor ${upn} is: ${wachtwoord}\nBij het inloggen (https://outlook.office.com) kies je direct een eigen wachtwoord.`
    : `Hoi ${vn}, je tijdelijke wachtwoord voor ${upn} is: ${wachtwoord}\nBij de eerste keer inloggen kies je zelf een nieuw wachtwoord. Alle uitleg staat in de mail die je net van mij kreeg.`;
}

/** mailto-link naar het privé-adres (zonder wachtwoord). */
export function mailtoLink(aan, { onderwerp, tekst }) {
  return `mailto:${encodeURIComponent(aan || '')}?subject=${encodeURIComponent(onderwerp)}&body=${encodeURIComponent(tekst)}`;
}
